<template>
  <div class="p-0">
    <div class="flex items-center justify-between mb-6 gap-4">
      <h1 class="text-2xl font-bold text-gray-800">Tareas creadas por mí</h1>
      <div class="flex gap-2">
        <button
          @click="goToBatches"
          class="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
        >
          Ver lotes (tabla)
        </button>
        <button
          @click="goToCreateBatch"
          class="px-4 py-2 text-gray-200 bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          + Nuevo Lote
        </button>
      </div>
    </div>

    <!-- Filters row -->
    <div class="flex flex-col md:flex-row gap-3 mb-4">
      <div class="w-full md:w-56">
        <label class="block text-xs font-medium text-gray-500 mb-1">Estado</label>
        <select
          v-model="estadoFilter"
          @change="applyFilters"
          class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
        >
          <option value="">Todos</option>
          <option value="en_curso">En curso</option>
          <option value="finalizadas">Finalizadas</option>
        </select>
      </div>

      <div class="w-full md:w-64">
        <label class="block text-xs font-medium text-gray-500 mb-1">Responsable</label>
        <select
          v-model="responsableFilter"
          @change="applyFilters"
          class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
        >
          <option value="">Todos</option>
          <option v-for="u in teachers" :key="u.id" :value="u.id">
            {{ teacherLabel(u.id) }}
          </option>
        </select>
      </div>

      <div class="w-full md:w-64">
        <label class="block text-xs font-medium text-gray-500 mb-1">Categoría</label>
        <select
          v-model="categoryFilter"
          @change="applyFilters"
          class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
        >
          <option value="">Todas</option>
          <option v-for="c in categories" :key="c.id" :value="c.id">
            {{ c.name }}
          </option>
        </select>
      </div>

      <div class="w-full md:w-56">
        <label class="block text-xs font-medium text-gray-500 mb-1">Prioridad</label>
        <select
          v-model="priorityFilter"
          @change="applyFilters"
          class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
        >
          <option value="">Todas</option>
          <option value="baja">Baja</option>
          <option value="media">Media</option>
          <option value="alta">Alta</option>
          <option value="urgente">Urgente</option>
        </select>
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex flex-wrap gap-2 mb-4">
      <button @click="estadoTab = 'todas'" :class="tabClass(estadoTab === 'todas')">Todas</button>
      <button @click="estadoTab = 'en_curso'" :class="tabClass(estadoTab === 'en_curso')">En curso</button>
      <button @click="estadoTab = 'finalizadas'" :class="tabClass(estadoTab === 'finalizadas')">Finalizadas</button>
    </div>

    <div v-if="loading" class="py-10">
      <Loading />
    </div>

    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
      {{ error }}
    </div>

    <div v-else-if="filteredBatches.length === 0" class="bg-gray-50 border border-gray-200 rounded-md p-6 text-center text-gray-600">
      No hay lotes con los filtros seleccionados.
    </div>

    <div v-else class="space-y-4">
      <div
        v-for="b in filteredBatches"
        :key="b.id"
        class="bg-white border border-gray-200 rounded-lg p-4"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <span class="px-2 py-1 text-xs font-semibold rounded-full bg-gray-50 border border-gray-200 text-gray-700">
              {{ batchStatusLabel(b) }}
            </span>
            <div class="mt-2 text-lg font-semibold text-gray-900">
              {{ b.title || '-' }}
            </div>
            <div class="mt-1 text-sm text-gray-600">
              {{ batchCategoryName(b) }}
            </div>
          </div>

          <span class="px-2 py-1 text-xs font-semibold rounded-full" :class="priorityClass(batchPriority(b))">
            {{ priorityLabel(batchPriority(b)) }}
          </span>
        </div>

        <!-- Progress bar -->
        <div class="mt-4">
          <div class="flex items-center justify-between text-xs text-gray-500 mb-2">
            <span>Progreso del lote</span>
            <span class="font-medium text-gray-800">
              {{ b.statsCompleted }}/{{ b.statsTotal }} completadas
            </span>
          </div>
          <div class="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div class="h-2.5 bg-indigo-600" :style="{ width: batchProgressPercent(b) + '%' }"></div>
          </div>
        </div>

        <div class="mt-3 flex items-center justify-between gap-3">
          <div class="text-sm text-gray-600 truncate">
            Responsable:
            <span class="font-medium text-gray-900">{{ batchResponsibleLabel(b) }}</span>
          </div>

          <div class="flex gap-2">
            <button
              @click="openBatchTasks(b.id)"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Detalle
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TasksCreatedByMeView',
  data() {
    return {
      loading: false,
      error: null,

      batches: [],
      categories: [],
      teachers: [],

      estadoTab: 'todas',
      estadoFilter: '',
      responsableFilter: '',
      categoryFilter: '',
      priorityFilter: '',
    };
  },
  computed: {
    filteredBatches() {
      let list = [...this.batches];

      // Tab filter
      if (this.estadoTab === 'en_curso') {
        list = list.filter((b) => this.batchStatusValue(b) === 'en_curso');
      } else if (this.estadoTab === 'finalizadas') {
        list = list.filter((b) => this.batchStatusValue(b) === 'finalizadas');
      }

      // Dropdown filters
      if (this.estadoFilter) {
        list = list.filter((b) => this.batchStatusValue(b) === this.estadoFilter);
      }
      if (this.responsableFilter) {
        list = list.filter((b) => String(this.batchPrimaryResponsibleId(b)) === String(this.responsableFilter));
      }
      if (this.categoryFilter) {
        list = list.filter((b) => String(b.category_id || b.category?.id) === String(this.categoryFilter));
      }
      if (this.priorityFilter) {
        list = list.filter((b) => String(this.batchPriority(b)) === String(this.priorityFilter));
      }

      return list;
    },
  },
  created() {
    this.fetchAll();
  },
  methods: {
    unwrapData(resp) {
      return resp?.data?.data ?? resp?.data?.data?.data ?? resp?.data ?? [];
    },
    async fetchAll() {
      this.loading = true;
      this.error = null;
      try {
        const [catsRes, teachersRes, batchesRes] = await Promise.all([
          this.$http.get('/task-categories', { params: { active_only: true } }),
          this.$http.get('/teachers'),
          this.$http.get('/task-batches'),
        ]);

        const catsPayload = this.unwrapData(catsRes);
        const teachersPayload = this.unwrapData(teachersRes);
        const batchesPayload = this.unwrapData(batchesRes);

        this.categories = Array.isArray(catsPayload) ? catsPayload : catsPayload?.data || [];
        this.teachers = Array.isArray(teachersPayload) ? teachersPayload : teachersPayload?.data || [];
        const baseBatches = Array.isArray(batchesPayload) ? batchesPayload : batchesPayload?.data || [];

        const enriched = await Promise.all(
          baseBatches.map(async (b) => {
            try {
              const statsRes = await this.$http.get(`/task-batches/${b.id}`);
              const statsPayload = this.unwrapData(statsRes);
              const completed = Number(statsPayload?.completed ?? statsPayload?.completadas ?? b.completed ?? b.completadas ?? 0);
              const pending = Number(statsPayload?.pending ?? statsPayload?.pendientes ?? b.pending ?? b.pendientes ?? 0);
              const expired = Number(statsPayload?.expired ?? statsPayload?.vencidas ?? b.expired ?? b.vencidas ?? 0);
              const canceled = Number(statsPayload?.cancelled ?? statsPayload?.canceladas ?? b.cancelled ?? b.canceladas ?? 0);

              return {
                ...b,
                statsCompleted: completed,
                statsPending: pending,
                statsExpired: expired,
                statsCanceled: canceled,
                statsTotal: completed + pending + expired + canceled,
              };
            } catch {
              return {
                ...b,
                statsCompleted: 0,
                statsPending: 0,
                statsExpired: 0,
                statsCanceled: 0,
                statsTotal: 0,
              };
            }
          })
        );

        this.batches = enriched;
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Error al cargar lotes';
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      // Computed se encarga del filtrado.
    },
    tabClass(isActive) {
      return [
        'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
        isActive ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
      ];
    },
    teacherLabel(id) {
      const t = this.teachers.find((x) => String(x.id) === String(id));
      if (!t) return id;
      const lname = t.Profile?.lastname || '';
      const fname = t.Profile?.firstname || '';
      return (lname || fname) ? `${lname}, ${fname}`.trim() : t.name || t.email || String(id);
    },
    batchProgressPercent(batch) {
      const total = Number(batch.statsTotal || 0);
      if (total <= 0) return 0;
      return Math.round((Number(batch.statsCompleted || 0) / total) * 100);
    },
    batchStatusValue(batch) {
      const total = Number(batch.statsTotal || 0);
      const completed = Number(batch.statsCompleted || 0);
      if (total > 0 && completed === total) return 'finalizadas';
      return 'en_curso';
    },
    batchStatusLabel(batch) {
      return this.batchStatusValue(batch) === 'finalizadas' ? 'Finalizada' : 'En curso';
    },
    priorityLabel(priority) {
      const p = (priority || '').toLowerCase();
      const map = { baja: 'Baja', media: 'Media', alta: 'Alta', urgente: 'Urgente' };
      return map[p] || priority || '-';
    },
    priorityClass(priority) {
      const p = (priority || '').toLowerCase();
      const map = {
        baja: 'bg-blue-50 text-blue-700 border-blue-100',
        media: 'bg-indigo-50 text-indigo-700 border-indigo-100',
        alta: 'bg-orange-50 text-orange-700 border-orange-100',
        urgente: 'bg-red-50 text-red-700 border-red-100',
      };
      return map[p] || 'bg-gray-100 text-gray-800 border-gray-200';
    },
    batchPriority(batch) {
      return batch.default_priority || batch.priority || batch.defaultPriority || 'media';
    },
    batchCategoryName(batch) {
      const id = batch?.category_id || batch?.category?.id;
      if (!id) return '-';
      const c = this.categories.find((x) => String(x.id) === String(id));
      return c?.name || '-';
    },
    batchPrimaryResponsibleId(batch) {
      // Si existe lista de responsables, usamos el primero.
      const list = batch?.responsible_user_ids || batch?.responsibleUserIds || [];
      return Array.isArray(list) && list.length ? list[0] : (batch.responsible_user_id || batch.responsibleUserId || '');
    },
    batchResponsibleLabel(batch) {
      const uid = this.batchPrimaryResponsibleId(batch);
      if (!uid) return '-';
      return this.teacherLabel(uid);
    },
    goToBatches() {
      this.$router.push('/task-batches');
    },
    goToCreateBatch() {
      this.$router.push({ path: '/tasks/create', query: { batch: '1' } });
    },
    openBatchTasks(batchId) {
      if (!batchId) return;
      this.$router.push({ path: '/tasks', query: { batch_id: batchId } });
    },
  },
};
</script>

<style scoped>
</style>

